import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Check, X as XIcon, Clock } from "lucide-react";

const STATUS_STYLES = {
  pending: { label: "Pending", color: "bg-amber-50 text-amber-800 border-amber-200", icon: Clock },
  accepted: { label: "Accepted", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Check },
  rejected: { label: "Rejected", color: "bg-red-50 text-red-700 border-red-200", icon: XIcon },
};

export default function Applications() {
  const [scope, setScope] = useState("mine"); // 'mine' or 'received'
  const [items, setItems] = useState([]);

  const load = async () => {
    const { data } = await api.get("/applications", { params: { scope } });
    setItems(data);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [scope]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/applications/${id}`, { status });
      toast.success(`Marked as ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="applications-page">
      <div>
        <div className="tag"><span className="w-1.5 h-1.5 bg-[#94959A]" /> Pipeline</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3">Applications</h1>
        <p className="text-zinc-600 text-sm mt-1">Track your applications and decisions in one place.</p>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setScope("mine")}
          data-testid="apps-scope-mine"
          className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border ${
            scope === "mine" ? "bg-[#09090b] text-white border-[#09090b]" : "border-zinc-300 hover:bg-zinc-100"
          }`}
        >Submitted by me</button>
        <button
          onClick={() => setScope("received")}
          data-testid="apps-scope-received"
          className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border ${
            scope === "received" ? "bg-[#09090b] text-white border-[#09090b]" : "border-zinc-300 hover:bg-zinc-100"
          }`}
        >Received on my posts</button>
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="applications-table">
          <thead>
            <tr className="text-left text-zinc-500 overline border-b border-zinc-200">
              <th className="p-3">Opportunity</th>
              <th className="p-3">{scope === "mine" ? "Owner" : "Applicant"}</th>
              <th className="p-3">{scope === "mine" ? "Role" : "Applicant role"}</th>
              <th className="p-3">Message</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No applications.</td></tr>
            )}
            {items.map((a) => {
              const S = STATUS_STYLES[a.status] || STATUS_STYLES.pending;
              const Icon = S.icon;
              return (
                <tr key={a.application_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`app-row-${a.application_id}`}>
                  <td className="p-3 font-medium">{a.opportunity_title}</td>
                  <td className="p-3 text-zinc-700">{scope === "mine" ? "—" : a.applicant_name}</td>
                  <td className="p-3 text-zinc-600 uppercase text-xs tracking-widest">{a.applicant_role || "—"}</td>
                  <td className="p-3 text-zinc-600 max-w-[280px] truncate">{a.message || "—"}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border px-2 py-0.5 ${S.color}`}>
                      <Icon className="w-3 h-3" /> {S.label}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    {scope === "received" && a.status === "pending" && (
                      <>
                        <button onClick={() => updateStatus(a.application_id, "accepted")} data-testid={`app-accept-${a.application_id}`} className="px-2 py-1 hover:bg-emerald-50 text-emerald-700 text-xs font-semibold">
                          Accept
                        </button>
                        <button onClick={() => updateStatus(a.application_id, "rejected")} data-testid={`app-reject-${a.application_id}`} className="px-2 py-1 hover:bg-red-50 text-red-600 text-xs font-semibold">
                          Reject
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
